/**
 * Renderer de markdown mínimo para documentos legales (Sprint 14 S14.2).
 *
 * No añade dependencia de markdown (el proyecto no la tenía y para dos
 * documentos estáticos no compensa). Soporta SOLO los constructos presentes
 * en los textos legales:
 *   - Encabezados `#` (h1) y `##` (h2)
 *   - Cita `>` (la nota de borrador), agrupando líneas consecutivas
 *   - Listas no ordenadas `- `
 *   - Negrita `**texto**`
 *   - Enlaces `[texto](url)`
 *   - Regla horizontal `---`
 *   - Párrafo en cursiva (línea envuelta en `*...*`) → pie de página discreto
 *
 * Importante: el patrón de enlace EXIGE `[texto](url)`. Un corchete suelto
 * como `[FECHA]` o `[RESPONSABLE — ...]` NO casa con enlace ni con negrita,
 * así que se renderiza literal (justo lo que queremos para los placeholders).
 */
import type { ReactNode } from "react";

// Negrita `**...**` O enlace `[texto](url)`. Grupos:
//   1 = contenido negrita · 2 = texto enlace · 3 = url enlace
const INLINE_RE = /\*\*([^*]+)\*\*|\[([^\]]+)\]\(([^)]+)\)/g;

/** Convierte el formato inline (negrita + enlaces) de una línea en nodos. */
function renderInline(text: string): ReactNode[] {
  const nodes: ReactNode[] = [];
  let last = 0;
  let key = 0;
  let m: RegExpExecArray | null;
  INLINE_RE.lastIndex = 0;
  while ((m = INLINE_RE.exec(text)) !== null) {
    if (m.index > last) nodes.push(text.slice(last, m.index));
    if (m[1] !== undefined) {
      // **negrita**
      nodes.push(
        <strong key={key++} className="font-medium text-bone">
          {m[1]}
        </strong>,
      );
    } else {
      // [texto](url) — m[2]=texto, m[3]=url
      const url = m[3];
      const external = /^https?:\/\//i.test(url);
      nodes.push(
        <a
          key={key++}
          href={url}
          className="text-lime underline underline-offset-2 hover:text-lime/90"
          {...(external
            ? { target: "_blank", rel: "noopener noreferrer" }
            : {})}
        >
          {m[2]}
        </a>,
      );
    }
    last = m.index + m[0].length;
  }
  if (last < text.length) nodes.push(text.slice(last));
  return nodes;
}

/** ¿Línea íntegramente en cursiva (`*...*`) y NO negrita (`**...**`)? */
function isItalicLine(line: string): boolean {
  return (
    line.startsWith("*") &&
    !line.startsWith("**") &&
    line.endsWith("*") &&
    !line.endsWith("**") &&
    line.length > 2
  );
}

function parseBlocks(markdown: string): ReactNode[] {
  const lines = markdown.replace(/\r\n/g, "\n").split("\n");
  const out: ReactNode[] = [];
  let key = 0;
  let i = 0;

  while (i < lines.length) {
    const line = lines[i].trimEnd();

    // Línea en blanco → separador.
    if (line.trim() === "") {
      i++;
      continue;
    }

    // Regla horizontal.
    if (line.trim() === "---") {
      out.push(<hr key={key++} className="my-8 border-border-subtle" />);
      i++;
      continue;
    }

    // Encabezados.
    if (line.startsWith("## ")) {
      out.push(
        <h2 key={key++} className="mt-8 text-lg font-medium text-bone">
          {renderInline(line.slice(3))}
        </h2>,
      );
      i++;
      continue;
    }
    if (line.startsWith("# ")) {
      out.push(
        <h1
          key={key++}
          className="text-2xl font-medium tracking-tight text-bone"
        >
          {renderInline(line.slice(2))}
        </h1>,
      );
      i++;
      continue;
    }

    // Cita en bloque (nota de borrador): agrupar líneas `>` consecutivas.
    if (line.startsWith(">")) {
      const buf: string[] = [];
      while (i < lines.length && lines[i].trimEnd().startsWith(">")) {
        buf.push(lines[i].trimEnd().replace(/^>\s?/, ""));
        i++;
      }
      out.push(
        <blockquote
          key={key++}
          className="mt-4 rounded-md border border-border-subtle bg-carbon-raised px-4 py-3 text-xs text-bone-muted"
        >
          {renderInline(buf.join(" "))}
        </blockquote>,
      );
      continue;
    }

    // Lista no ordenada: agrupar líneas `- ` consecutivas.
    if (line.startsWith("- ")) {
      const items: string[] = [];
      while (i < lines.length && lines[i].trimEnd().startsWith("- ")) {
        items.push(lines[i].trimEnd().slice(2));
        i++;
      }
      out.push(
        <ul
          key={key++}
          className="mt-3 list-disc space-y-1 pl-5 text-sm leading-relaxed text-bone-muted"
        >
          {items.map((it, idx) => (
            <li key={idx}>{renderInline(it)}</li>
          ))}
        </ul>,
      );
      continue;
    }

    // Párrafo íntegramente en cursiva → pie discreto.
    if (isItalicLine(line)) {
      out.push(
        <p key={key++} className="mt-6 text-xs italic text-gray-mid">
          {renderInline(line.slice(1, -1))}
        </p>,
      );
      i++;
      continue;
    }

    // Párrafo normal (puede contener negrita / enlaces inline).
    out.push(
      <p key={key++} className="mt-3 text-sm leading-relaxed text-bone-muted">
        {renderInline(line)}
      </p>,
    );
    i++;
  }

  return out;
}

interface Props {
  source: string;
}

/** Renderiza un documento markdown legal como JSX estructurado. */
export function LegalMarkdown({ source }: Props) {
  return <>{parseBlocks(source)}</>;
}
